import React, { useEffect, useRef } from 'react';
import { Animated } from 'react-native-web';
import { Box } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import { CloseIcon, ProAvatarPlaceholderIcon } from '../../../ui/Icons';

import {
  HeaderText,
  Stack,
  Button,
} from '../../../ui';

const SlideUpView = props => {
  const animation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(
      animation, {
        toValue: 100,
        duration: 500,
      },
    ).start();
  }, [animation]);

  return (
    <Animated.View
      style={{
        ...props.style,
        marginBottom: animation.interpolate({
          inputRange: [0, 100],
          outputRange: ['-100%', '0%'],
        }),
        opacity: animation.interpolate({
          inputRange: [0, 100],
          outputRange: [0.5, 1],
        }),
        flex: 1,
      }}
    >
      {props.children}
    </Animated.View>
  );
};

type ProTabBarToasterProps = {
  /** Color for the background */
  backgroundColor?: string;
  /** Color for the message of the box */
  messageColor?: string;
  /** An icon to display above the text */
  icon?: React.ReactNode;
  /** A message to display above the CTA */
  message?: string;
  /** Text to display in the CTA */
  ctaText?: string;
  /** CTA appearance (matches button appearance options) */
  ctaAppearance?: React.ComponentProps<typeof Button>['appearance'];
  /** CTA size (matchs button sizes) */
  ctaSize?: 'small' | 'medium' | 'large';
  /** Called when the CTA button is clicked */
  onClick?: () => void;
  /** Called when the close button is clicked */
  onClose?: () => void;
};

export const ProTabBarToaster = ({
  backgroundColor = colors.whiteWash,
  messageColor = colors.blackInBlack,
  icon,
  message,
  ctaText,
  ctaAppearance = 'text-primary',
  ctaSize = 'large',
  onClick,
  onClose,
}: ProTabBarToasterProps) => {
  const renderIcon = () => {
    switch (icon) {
      case 'proAvatarPlaceholder':
        return (
          <Box paddingBottom={16}>
            <ProAvatarPlaceholderIcon width={48} height={48} />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      color={backgroundColor}
      style={{
        width: '100%',
        zIndex: 1,
        shadowColor: 'rgba(0, 0, 0, 0.24)',
        shadowRadius: 4,
        shadowOffset: { width: 0, height: -4 },
      }}
    >
      <SlideUpView>
        <Button
          onClick={onClose}
          appearance="none"
          size="medium"
          style={{
            position: 'absolute',
            right: 0,
            paddingTop: 16,
            paddingRight: 16,
            zIndex: 1,
          }}
          testID="pro-tab-bar-toaster-close-btn"
        >
          <CloseIcon
            color={colors.ash}
            height={16}
            width={16}
          />
        </Button>
        <Stack
          alignItems="center"
          flex={1}
          justifyContent="space-between"
          paddingX={24}
          paddingTop={24}
          paddingBottom={8}
        >
          {renderIcon()}
          <HeaderText
            level={2}
            textAlign="center"
            color={messageColor}
          >
            {message}
          </HeaderText>
          <Button
            appearance={ctaAppearance}
            size={ctaSize}
            onClick={onClick}
          >
            {ctaText}
          </Button>
        </Stack>
      </SlideUpView>
    </Box>
  );
};
